import { useState, useRef, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCheck,
  faChevronDown,
  faGlobe,
  faMagnifyingGlass,
  faUser,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

export interface SpaceOption {
  key: string;
  name: string;
  type?: 'global' | 'personal' | 'knowledge_base' | string;
}

interface Props {
  value: string;
  onChange: (key: string) => void;
  spaces: SpaceOption[];
  isDark: boolean;
  showAllOption?: boolean;
  placeholder?: string;
}

export function ConfluenceSpaceCombobox({ value, onChange, spaces, isDark, showAllOption = false, placeholder = 'Select space…' }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const globalSpaces = useMemo(() =>
    spaces.filter(s => s.type !== 'personal').sort((a, b) => a.name.localeCompare(b.name)),
    [spaces]
  );
  const personalSpaces = useMemo(() =>
    spaces.filter(s => s.type === 'personal').sort((a, b) => a.name.localeCompare(b.name)),
    [spaces]
  );

  const filterFn = (s: SpaceOption) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.key.toLowerCase().includes(q);
  };

  const filteredGlobal = globalSpaces.filter(filterFn);
  const filteredPersonal = personalSpaces.filter(filterFn);

  const selected = value === '__ALL__'
    ? { name: 'All Spaces (combined)', key: '__ALL__', type: 'global' as const }
    : spaces.find(s => s.key === value);

  const label = selected?.name ?? placeholder;

  function renderSpaceItem(s: SpaceOption) {
    const isSelected = s.key === value;
    return (
      <button
        key={s.key}
        onClick={() => { onChange(s.key); setOpen(false); setSearch(''); }}
        className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
          isSelected
            ? isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-50 text-blue-600'
            : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
          isSelected ? 'bg-blue-500 border-blue-500' : isDark ? 'border-slate-600' : 'border-gray-300'
        }`}>
          {isSelected && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />}
        </div>
        <span className="flex-1 truncate">{s.name}</span>
        {s.type !== 'personal' && (
          <span className={`flex-shrink-0 text-[10px] font-mono ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{s.key}</span>
        )}
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg border transition-colors text-left ${
          value
            ? isDark ? 'border-sky-500/40 bg-sky-500/10 text-sky-200' : 'border-sky-500/40 bg-sky-50 text-sky-700'
            : isDark ? 'bg-white/[0.03] border-white/[0.08] text-slate-300 hover:border-slate-500' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
        }`}>
          {value === '__ALL__'
            ? <FontAwesomeIcon icon={faBuilding} className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
            : <FontAwesomeIcon icon={faGlobe} className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />}
          <span className="flex-1 truncate">{label}</span>
          <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 flex-shrink-0 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="!w-72 !p-0 overflow-hidden !z-[9999]">
        {/* Search */}
        <div className={`p-2 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'}`}>
          <div className="relative">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={searchRef}
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search spaces…"
              className={`w-full pl-7 pr-2 py-1.5 text-xs rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isDark ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`}
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto overflow-x-hidden py-1">
          {/* None option */}
          {(!search) && (
            <button
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
                value === ''
                  ? isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-50 text-blue-600'
                  : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
                value === '' ? 'bg-blue-500 border-blue-500' : isDark ? 'border-slate-600' : 'border-gray-300'
              }`}>
                {value === '' && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />}
              </div>
              <span className="flex-1 italic opacity-60">None (optional)</span>
            </button>
          )}
          {/* All Spaces option */}
          {showAllOption && (!search || 'all spaces'.includes(search.toLowerCase())) && (
            <button
              onClick={() => { onChange('__ALL__'); setOpen(false); setSearch(''); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
                value === '__ALL__'
                  ? isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-50 text-blue-600'
                  : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
                value === '__ALL__' ? 'bg-blue-500 border-blue-500' : isDark ? 'border-slate-600' : 'border-gray-300'
              }`}>
                {value === '__ALL__' && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />}
              </div>
              <FontAwesomeIcon icon={faBuilding} className="h-3 w-3 opacity-50" />
              <span className="flex-1">All Spaces (combined)</span>
            </button>
          )}
          {/* Global/knowledge_base spaces */}
          {filteredGlobal.length > 0 && (
            <>
              <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                Spaces
              </div>
              {filteredGlobal.map(renderSpaceItem)}
            </>
          )}
          {/* Personal spaces */}
          {filteredPersonal.length > 0 && (
            <>
              <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                <FontAwesomeIcon icon={faUser} className="h-2.5 w-2.5" /> People
              </div>
              {filteredPersonal.map(renderSpaceItem)}
            </>
          )}
          {filteredGlobal.length === 0 && filteredPersonal.length === 0 && search && (
            <div className="px-3 py-4 text-xs text-center text-gray-400">No matches</div>
          )}
        </div>
        {/* Clear */}
        {value && value !== '' && (
          <div className={`border-t px-2 py-1.5 ${isDark ? 'border-slate-700/50' : 'border-gray-100'}`}>
            <button
              onClick={() => { onChange(''); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-2 py-1 text-xs rounded transition-colors ${isDark ? 'text-red-400 hover:bg-slate-800' : 'text-red-500 hover:bg-gray-50'}`}
            >
              <FontAwesomeIcon icon={faXmark} className="h-3 w-3" /> Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
